/* eslint-disable */
import React, { Fragment, useEffect, useContext } from 'react';
import injectSheet from 'react-jss';
import styleSheet from './styles';
import CheckBox from '../CheckBox';
import { Button } from '../Buttons';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import deepEqual from 'deep-equal';
import arrayMove from 'Utils/arrayMove';
import { audienceProfileConfig } from '../../../store/audienceProfileConfig';
import { parentProfileConfig } from '../../../store/parentProfileConfig';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';

interface Props {
  sections: any;
  profileSections: any;
  setSections: (sections: any) => void;
  setProfileSections: (sections: any) => void;
  showConfigMenu: (val: any) => void;
  showToastMessage: any;
  visitorType: string;
  classes: any;
}

const onCheckBoxClick = (
  section: any,
  sections: any,
  setSections: (sections: any) => void,
  sectionItemLabel?: string
) => (e: { target: { value: string; id: string } }) => {
  //On clicking checkbox of a section header - contact, location, etc...
  if (!sectionItemLabel) {
    const showHide = !section.hidden;
    setSections(
      sections.map((sec: any) =>
        section.label === sec.label
          ? {
              ...sec,
              hidden: showHide,
              sectionItems: sec.sectionItems.map((item: any) => ({
                ...item,
                hidden: showHide,
              })),
            }
          : sec
      )
    );
  } else {
    //On clicking checkbox of a section item - first name, last name under Contact
    let checkCount = 0;
    setSections(
      sections.map((sec: any) => {
        return section.label === sec.label
          ? {
              ...sec,
              sectionItems: sec.sectionItems.map((item: any) => {
                if (sectionItemLabel === item.label) {
                  if (item.hidden) checkCount++;
                  return { ...item, hidden: !item.hidden };
                } else {
                  if (!item.hidden) checkCount++;
                  return item;
                }
              }),
              hidden: checkCount === 0 ? true : false,
            }
          : sec;
      })
    );
  }
};

const sectionNameLineWidthMap = {
  Contact: '170px',
  Location: '165px',
  Demographic: '130px',
  Academic: '140px',
  Score: '185px',
};

// SortableContainer and SortableItems for the section headings - Contact, Location, Demographic etc..
const SectionContainer = SortableContainer(({ props }: any) => {
  /* drag-drop-1 This dummy div is necessary between the container and the items - 
  https://github.com/clauderic/react-sortable-hoc/issues/367 */
  return (
    <div>
      {props.sections.map((section: any, index: any) => (
        <SectionHead
          key={`item-${section.label}`}
          index={index}
          i={index}
          section={section}
          props={props}
          sections={props.sections}
        />
      ))}
    </div>
  );
});

const SectionHead = SortableElement(({ section, props, sections, i }: any) => {
  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    props.setSections(
      sections.map((sec: any) =>
        section.label === sec.label
          ? {
              ...sec,
              sectionItems: arrayMove(sec.sectionItems, oldIndex, newIndex),
            }
          : sec
      )
    );
  };
  return (
    <div className={props.classes.section}>
      <div className={props.classes.justifyCenter}>
        <span
          className={props.classes.reposition}
          style={{
            visibility: props.sections.length === 1 ? 'hidden' : 'visible',
          }}
        ></span>
        <CheckBox
          isFocused={i === 0}
          onClick={onCheckBoxClick(
            section,
            props.sections,
            props.setSections,
            null
          )}
          id={section}
          isChecked={!section.hidden}
        />
        <span className={props.classes.sectionHeadName}>{section.label}</span>
        <span
          className={props.classes.sectionHeadLine}
          style={{ width: sectionNameLineWidthMap[section.label] }}
        />
      </div>
      <SectionItemContainer
        onSortEnd={onSortEnd}
        axis={'y'}
        lockAxis={'y'}
        lockToContainerEdges={true}
        lockOffset={props.sections.length === 1 ? '0px' : '100%'}
        distance={1}
        props={props}
        section={section}
        sectionItems={section.sectionItems}
      />
    </div>
  );
});

// SortableContainer and SortableItems for the section items - First Name, Last Name, etc.. under Contact
const SectionItemContainer = SortableContainer(
  ({ props, section, sectionItems }: any) => {
    /* drag-drop-1 This dummy div is necessary between the container and the items - 
  https://github.com/clauderic/react-sortable-hoc/issues/367 */
    return (
      <div>
        {sectionItems.map((sectionItem: any, index: any) => (
          <SectionItem
            key={`item-${sectionItem.label}`}
            index={index}
            sectionItem={sectionItem}
            props={props}
            section={section}
          />
        ))}
      </div>
    );
  }
);

const SectionItem = SortableElement(({ sectionItem, section, props }: any) => {
  return (
    <div className={props.classes.sectionItem}>
      <span className={props.classes.reposition}></span>
      <CheckBox
        onClick={onCheckBoxClick(
          section,
          props.sections,
          props.setSections,
          sectionItem.label
        )}
        label={sectionItem.label}
        id={sectionItem}
        isChecked={!sectionItem.hidden}
      />
    </div>
  );
});

const RevertToDefault: React.FC<Props> = ({
  classes,
  sectionsChanged,
  onClick,
}: any) => {
  const onKeyUp = (e: any) => {
    if (e.key.toLocaleLowerCase() === 'enter') onClick();
  };
  return (
    <a
      tabIndex={0}
      onKeyUp={onKeyUp}
      className={classes.revertToDefault}
      style={{
        opacity: !sectionsChanged ? 0.5 : 1,
      }}
      onClick={onClick}
    >
      Revert to Default
    </a>
  );
};

const ActionButtons: React.FC<Props> = ({
  sectionsChanged,
  setProfileSections,
  sections,
  showConfigMenu,
  showToastMessage,
}: any) => {
  const {
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const cancelClick = (e: any) => {
    // This is a Windows hack to focus on cog when cancel is clicked //
    // @todo refactor how the profile details page reveals the configuration menu. Currently seems overly complicated //
    const button = e.currentTarget.parentNode.parentNode.parentNode.firstChild.querySelector(
      'button'
    );
    if (button) button.focus();
    if (showConfigMenu) showConfigMenu(false);
  };
  const applyClick = (e: any) => {
    // Product Analytics tracking event
    track('audience_profile_fields_configured');
    // This is a Windows hack to focus on cog when cancel is clicked //
    // @todo refactor how the profile details page reveals the configuration menu. Currently seems overly complicated //
    const button = e.currentTarget.parentNode.parentNode.parentNode.parentNode.firstChild.querySelector(
      'button'
    );
    if (button) button.focus();

    setProfileSections(sections);
    if (showConfigMenu) showConfigMenu(false);
    showToastMessage();
  };
  return (
    <div style={{ marginTop: '12px', marginLeft: '30px' }}>
      <Button buttonType={'defaultClear'} onClick={cancelClick}>
        Cancel
      </Button>
      <span style={{ marginLeft: '20px' }}>
        <Button
          buttonType={'defaultBlue'}
          disabled={sectionsChanged ? false : true}
          onClick={applyClick}
        >
          Apply
        </Button>
      </span>
    </div>
  );
};

function handleKeyEvent(e: KeyboardEvent, showConfigMenu: any) {
  let key = e.key.toLowerCase();
  if (key === 'escape' && showConfigMenu) {
    showConfigMenu(false);
  }
}

const ProfileConfiguration: React.FC<Props> = props => {
  const config = props.visitorType.includes('parent')
    ? parentProfileConfig
    : audienceProfileConfig;
  const revertEnable = !deepEqual(props.sections, config, {
    strict: true,
  });
  const applyEnable = !deepEqual(props.sections, props.profileSections, {
    strict: true,
  });
  useEffect(() => {
    document.addEventListener('keydown', (e: any) => {
      if (props.showConfigMenu) handleKeyEvent(e, props.showConfigMenu);
    });
    return () => {
      document.removeEventListener('keydown', (e: any) => {
        if (props.showConfigMenu) handleKeyEvent(e, props.showConfigMenu);
      });
    };
  });

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    props.setSections(arrayMove(props.sections, oldIndex, newIndex));
  };

  return (
    <Fragment>
      {
        <div className={props.classes.container}>
          <SectionContainer
            onSortEnd={onSortEnd}
            axis={'y'}
            lockAxis={'y'}
            lockToContainerEdges={true}
            lockOffset={props.sections.length === 1 ? '0px' : '100%'}
            distance={1}
            props={props}
            sections={props.sections}
          />
          <RevertToDefault
            //@ts-ignore
            sectionsChanged={revertEnable}
            //Only if the sections have changed, revert to default
            onClick={() => {
              revertEnable && props.setSections(config);
            }}
            {...props}
          />
          <ActionButtons {...props} sectionsChanged={applyEnable} />
        </div>
      }
    </Fragment>
  );
};

export default injectSheet(styleSheet)(ProfileConfiguration);
